import { useMutation } from '@tanstack/react-query';
import { Action, cn, Label } from 'corso-admin-ui';
import {
  AllowedUploadMimeType,
  AllowedUploadMimeTypeLabel,
  defaultAcceptTypes,
  isAllowedFilename,
} from 'corso-types';
import { fileTypeFromBlob } from 'file-type';
import { useCallback, useRef, useState } from 'react';
import api, { UploadPath } from '~/api';
import { MediaThumbnail } from '~/components/media/MediaThumbnail';

export type MediaAsset = {
  name: string;
  src: string;
  isVisibleToCustomer?: boolean;
};

/** The `MediaAsset.src` will be an object URL representing the file that must be revoked/freed to prevent memory leaks. */
type PreviewAsset = MediaAsset & { file: File };

type InvalidAsset = { name: string; error: string };

type AssetDisplayProps = {
  asset: MediaAsset;
  onRemove: () => void;
  className?: string;
};

function RemovableMediaDisplay({
  asset,
  onRemove,
  className,
}: AssetDisplayProps) {
  return (
    <div className={cn('relative size-16', className)}>
      <MediaThumbnail src={asset.src} alt={asset.name} />
      <div className="absolute top-1 right-1">
        <Action
          icon="close"
          accessibilityLabel={`Remove ${asset.name}`}
          onClick={onRemove}
        />
      </div>
    </div>
  );
}

// very similar to the `InputTypeProps` type
type MediaUploaderProps = {
  id: string;
  label: string;
  labelVisuallyHidden?: boolean;
  // ? possibly add `details`
  required?: boolean;
  assets?: MediaAsset[];
  onChange: (assets: MediaAsset[]) => void;
  /** Determines the bucket prefix the signed URL uploads to. */
  uploadPath: UploadPath;
  accept?: readonly AllowedUploadMimeType[];
  maxAssets?: number;
};

/**
 * Acts as a wrapper for strictly uncontrolled input for multiple images.
 * Manages previewing, selection, and uploading images via presigned URLs.
 * In the event an image is removed, the component does **NOT** handle deleting the image from the bucket.
 * Resulting in an array of metadata image objects represented the uploaded images.
 */
export default function MediaUploader({
  id,
  label,
  labelVisuallyHidden = false,
  required = false,
  assets = [],
  onChange,
  uploadPath,
  accept = defaultAcceptTypes,
  maxAssets = Infinity,
}: MediaUploaderProps) {
  const ref = useRef<HTMLInputElement>(null);
  // images that are currently being uploaded, shown using ObjectURLs
  const [previewAssets, setPreviewAssets] = useState<PreviewAsset[]>([]);
  const [invalidAssets, setInvalidAssets] = useState<InvalidAsset[]>([]);

  const removePreviewAsset = useCallback((preview: PreviewAsset) => {
    URL.revokeObjectURL(preview.src);
    setPreviewAssets((previews) => previews.filter((p) => p !== preview));
  }, []);

  const { mutate: uploadAsset, isPending } = useMutation({
    mutationFn: (previews: PreviewAsset[]) =>
      // TODO handle upload errors for each file
      Promise.all(
        previews.map(({ file }) => api.fileUpload.put(file, uploadPath)),
      ),
    onSuccess: (results, previews) => {
      onChange([...assets, ...results]); // add uploaded image to the list of images
      previews.map(removePreviewAsset); // remove the preview from the list of previews, it should now be in the list of images
    },
    onError: (_, previews) => {
      previews.map(removePreviewAsset);
      setInvalidAssets((invalid) => [
        ...invalid,
        ...previews.map(({ name }) => ({
          name,
          error: 'Upload failed. Please try again.',
        })),
      ]);
    },
  });

  const clickInput = useCallback(() => ref.current?.click(), [ref]);

  const validateAndUpload = useCallback(
    async (files: File[]) => {
      const validated = await Promise.all(
        files.map(async (file) => {
          const fileType = await fileTypeFromBlob(file);
          const acceptedTypes: readonly string[] = accept;
          const isTypeAccepted =
            !!fileType && acceptedTypes.includes(fileType.mime);
          const isFilenameAllowed = isAllowedFilename(file.name);
          return { file, isTypeAccepted, isFilenameAllowed } as const;
        }),
      );

      setInvalidAssets(
        validated
          .filter((v) => !v.isTypeAccepted || !v.isFilenameAllowed)
          .map(({ file, isTypeAccepted }) => {
            if (!isTypeAccepted) {
              return {
                name: file.name,
                error: `File type not accepted. Accepted types: ${accept.map((type) => AllowedUploadMimeTypeLabel[type]).join(', ')}.`,
              };
            }
            console.error('Invalid Filename', {
              name: file.name,
              type: file.type,
            });
            return { name: file.name, error: 'Filename not allowed.' };
          }),
      );

      const acceptedFiles = validated
        .filter((v) => v.isTypeAccepted && v.isFilenameAllowed)
        .map(({ file }) => file);
      const remaining = Math.max(maxAssets - assets.length, 0);
      const validFiles = acceptedFiles.slice(0, remaining);

      setInvalidAssets((invalid) => [
        ...invalid,
        ...acceptedFiles.slice(remaining).map((file) => ({
          name: file.name,
          error: `Only ${maxAssets} files can be added.`,
        })),
      ]);

      if (!validFiles.length) return;

      const previews = validFiles.map(
        (file) =>
          ({
            name: file.name,
            src: URL.createObjectURL(file),
            file,
          }) satisfies PreviewAsset,
      );

      setPreviewAssets(previews);
      uploadAsset(previews);
    },
    [accept, assets.length, maxAssets, uploadAsset],
  );

  const triggerUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { target } = event;
      if (!target.files) return;
      const files = [...target.files];
      target.value = ''; // reset the input value to allow re-uploading the same file

      // oxlint-disable-next-line typescript/no-floating-promises -- ignored as we would not want to await this handler
      validateAndUpload(files);
    },
    [validateAndUpload],
  );

  const removeAsset = useCallback(
    (remove: MediaAsset) =>
      onChange(assets.filter((asset) => asset !== remove)),
    [assets, onChange],
  );

  return (
    <div className="flex flex-col gap-2">
      <div
        className={cn('flex justify-between', labelVisuallyHidden && 'sr-only')}
      >
        <Label htmlFor={id}>{label}</Label>
        {!required && (
          <span
            className="text-xs leading-6 text-neutral-500"
            id="images-optional"
          >
            Optional
          </span>
        )}
      </div>
      {!!(assets.length || previewAssets.length) && (
        <ul className="mt-2 flex flex-wrap gap-2">
          {assets.map((asset) => (
            <li key={asset.name}>
              <RemovableMediaDisplay
                asset={asset}
                onRemove={() => removeAsset(asset)}
              />
            </li>
          ))}
          {previewAssets.map((asset) => (
            <li key={asset.src}>
              <RemovableMediaDisplay
                asset={asset}
                onRemove={() => removePreviewAsset(asset)}
              />
            </li>
          ))}
        </ul>
      )}
      {!!invalidAssets.length && (
        <ul className="flex flex-col gap-1">
          {invalidAssets.map((invalid) => (
            <li
              key={invalid.name}
              role="alert"
              className="text-xs text-red-600"
            >
              {invalid.name}: {invalid.error}
            </li>
          ))}
        </ul>
      )}
      <Action
        icon="image"
        onClick={clickInput}
        loading={isPending}
        disabled={assets.length >= maxAssets}
      >
        Upload
      </Action>
      <input
        id={id}
        type="file"
        accept={accept.join(',')}
        hidden
        multiple
        required={required}
        onChange={triggerUpload}
        ref={ref}
      />
    </div>
  );
}
